import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import Field from "../../../shared/ui/Field/Field";
import Textarea from "../../../shared/ui/Field/Textarea";
import InlineError from "../../../shared/ui/Field/InlineError";
import Spinner from "../../../shared/ui/Loading/Spinner";
import EmptyState from "../../../shared/ui/EmptyState/EmptyState";
import Heading from "../../../shared/ui/Typography/Heading";
import Text from "../../../shared/ui/Typography/Text";

import { fetchClient, patchClient } from "../api/clients";
import { fetchCommunications } from "../api/communications";
import {
  parseDrfFieldErrors,
  messageFromError,
} from "../../../shared/api/drfErrors";

// ClientActivityScreenContent — Phase 16 stale-data tolerance widget.
//
// Validates: explicit stale-data tolerance WITHOUT synchronization
// infrastructure. No React Query, no RTK Query, no cache invalidation,
// no stale-while-revalidate, no polling, no background refresh, no
// normalized entity stores, no freshness registries, no coordination
// hooks, no useEntity / useResource / useCoordinator / useFreshness.
//
// Freshness ownership is screen-local, explicit, manual, passive. After
// EVERY successful client PATCH the widget flags `stale=true` and
// surfaces a manual refresh CTA next to the activity list. The user
// decides when to refresh. The list openly admits it may be outdated.
//
// `activityLoadedAt` is a feature-local implementation detail used to
// render a "Last loaded at HH:MM" timestamp on the activity pane —
// NOT a freshness primitive, NOT an invalidation key, NOT a
// synchronization abstraction (per brief "VERY IMPORTANT" note).

const NOTES_MAX = 2000;
const EDITABLE_KEYS = ["notes"];

const extractNotes = (client) =>
  client && typeof client.notes === "string" ? client.notes : "";

const validateNotes = (value) => {
  const errors = {};
  if ((value ?? "").length > NOTES_MAX) {
    errors.notes = `Не довше ${NOTES_MAX} символів.`;
  }
  return errors;
};

const formatTime = (epochMs) => {
  if (!epochMs) return "";
  const d = new Date(epochMs);
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
};

const COMMUNICATION_TYPE_LABELS = {
  call: "Дзвінок",
  sms: "SMS",
  email: "Email",
  meeting: "Зустріч",
  note: "Нотатка",
  other: "Інше",
};

const COMMUNICATION_DIRECTION_LABELS = {
  in: "Вхідне",
  out: "Вихідне",
  inbound: "Вхідне",
  outbound: "Вихідне",
};

const formatDateTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const yyyy = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mo}-${dd} ${formatTime(d.getTime())}`;
};

function ClientActivityScreenContent({ clientId }) {
  // ---------- Local freshness state (Step 3) ----------
  //
  // Plain useState slots. No reducer. No freshness engine. No
  // invalidation registry. No shared store. Every slot is owned
  // here, named explicitly, and visible top-to-bottom.

  const [client, setClient] = useState(null);
  const [communications, setCommunications] = useState([]);

  const [clientLoadState, setClientLoadState] = useState("loading");
  const [activityLoadState, setActivityLoadState] = useState("loading");

  const [patchState, setPatchState] = useState("idle");
  const [refreshState, setRefreshState] = useState("idle");

  const [stale, setStale] = useState(false);
  const [staleReason, setStaleReason] = useState(null);
  const [activityLoadedAt, setActivityLoadedAt] = useState(null);

  const [clientDraft, setClientDraft] = useState({ notes: "" });
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [activityError, setActivityError] = useState(null);

  // Phase 17 — navigation hint emitter. Bound to the secondary
  // "Save and return to list" submit only. Mutation and navigation
  // remain separate user intentions (the primary submit stays
  // in-place per Phase 16 stale-tolerance demo).
  const navigate = useNavigate();

  // ---------- Initial load (Step 4) ----------
  //
  // Sequential. Explicit. The client GET runs first; on success the
  // communications GET runs. Both write into the local state. No
  // Promise.all (the brief forbids it and the anti-infra audit
  // greps for it). No hydration framework. No preload system. The
  // screen owns its own loading lifecycle.
  //
  // Partial failure (Step 5): if the client GET succeeds but the
  // communications GET fails, the left pane still renders and the
  // right pane shows an error strip with a manual retry button.
  // The screen does NOT coordinate the two resources — they fail
  // and recover independently.

  useEffect(() => {
    if (clientId == null) {
      setClientLoadState("not-found");
      setActivityLoadState("error");
      return undefined;
    }

    let cancelled = false;

    setClientLoadState("loading");
    setActivityLoadState("loading");
    setLoadError(null);
    setActivityError(null);

    (async () => {
      let loadedClient;
      try {
        loadedClient = await fetchClient(clientId);
      } catch (err) {
        if (cancelled) return;
        const status = err && err.response && err.response.status;
        if (status === 403) {
          setClientLoadState("forbidden");
        } else if (status === 404) {
          setClientLoadState("not-found");
        } else {
          setLoadError(messageFromError(err));
          setClientLoadState("error");
        }
        setActivityLoadState("error");
        setActivityError("Клієнта не вдалося завантажити.");
        return;
      }
      if (cancelled) return;
      setClient(loadedClient);
      setClientDraft({ notes: extractNotes(loadedClient) });
      setClientLoadState("loaded");

      try {
        const list = await fetchCommunications(clientId);
        if (cancelled) return;
        setCommunications(Array.isArray(list) ? list : []);
        setActivityLoadedAt(Date.now());
        setActivityLoadState("loaded");
      } catch (err) {
        if (cancelled) return;
        setActivityError(messageFromError(err));
        setActivityLoadState("error");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [clientId]);

  // ---------- Notes edit handlers ----------

  const handleNotesChange = useCallback((event) => {
    const next = event.target.value;
    setClientDraft((prev) => ({ ...prev, notes: next }));
    setFieldErrors((prev) => {
      if (!prev.notes && !prev._form) return prev;
      const out = { ...prev };
      delete out.notes;
      delete out._form;
      return out;
    });
    setFormError(null);
    setPatchState((prev) => (prev === "saved" ? "idle" : prev));
  }, []);

  const baselineNotes = useMemo(() => extractNotes(client), [client]);
  const dirty = (clientDraft.notes ?? "") !== (baselineNotes ?? "");
  const saving = patchState === "saving";
  const refreshing = refreshState === "refreshing";
  const canSubmit = !saving && dirty;

  // ---------- PATCH flow (Step 7) ----------
  //
  // Pessimistic. The server is the source of truth. On success the
  // widget flips `stale=true` and surfaces a stale reason. The
  // activity list is NOT refreshed automatically — that is the
  // entire point of Phase 16. The user decides when (Step 8).
  //
  // On failure (Step 10): stale state is UNCHANGED. Field errors
  // render. No refresh prompt is shown.

  // Shared PATCH path. Returns true on success, false on failure /
  // skip. Local-only helper — NOT a coordinator, NOT a mutation
  // service, NOT shared with any other feature.
  const runPatch = async () => {
    if (!client || !dirty || saving) return false;

    const errs = validateNotes(clientDraft.notes);
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      setFormError(null);
      return false;
    }

    setFieldErrors({});
    setFormError(null);
    setPatchState("saving");

    try {
      const updated = await patchClient(clientId, {
        notes: clientDraft.notes ?? "",
      });
      setClient(updated);
      setClientDraft({ notes: extractNotes(updated) });
      setPatchState("saved");

      setStale(true);
      setStaleReason("Журнал взаємодій міг застаріти після редагування");
      return true;
    } catch (err) {
      const parsed = parseDrfFieldErrors(err, EDITABLE_KEYS);
      setFieldErrors(parsed);
      setFormError(parsed._form || null);
      setPatchState("error");
      return false;
    }
  };

  const handleSubmit = async (event) => {
    if (event) event.preventDefault();
    await runPatch();
  };

  // Phase 17 — secondary submit: save in place, then on success
  // navigate to the /clients list with an ephemeral route-state
  // stale hint. Mutation and navigation remain SEPARATE user
  // intentions — the primary submit (handleSubmit) keeps the
  // user on this screen so the Phase 16 stale-data-tolerance
  // demo is preserved unchanged.
  const handleSubmitAndReturn = async () => {
    const ok = await runPatch();
    if (ok) {
      navigate("/clients", {
        state: {
          mayBeStale: true,
          reason: "Дані могли застаріти після редагування нотаток",
        },
      });
    }
  };

  // ---------- Manual refresh flow (Step 8) ----------
  //
  // Explicit handler, single endpoint, pessimistic. Reads the
  // current activity list and resets `stale`. No coordinator. No
  // invalidation framework. No background sync. Refresh timing
  // belongs to the user.
  //
  // On failure (Step 10): `stale` REMAINS visible. The user
  // retries manually. No automatic recovery orchestration.

  const handleRefreshActivity = async () => {
    if (!clientId || refreshing) return;
    setRefreshState("refreshing");
    setActivityError(null);
    try {
      const list = await fetchCommunications(clientId);
      setCommunications(Array.isArray(list) ? list : []);
      setActivityLoadedAt(Date.now());
      setActivityLoadState("loaded");
      setRefreshState("idle");
      setStale(false);
      setStaleReason(null);
    } catch (err) {
      setActivityError(messageFromError(err));
      setRefreshState("error");
    }
  };

  // ---------- Render gates ----------

  if (clientLoadState === "loading") {
    return (
      <div className="feature-client-activity__state">
        <Spinner size="lg" label="Завантаження клієнта" />
      </div>
    );
  }

  if (clientLoadState === "forbidden") {
    return (
      <div className="feature-client-activity__state">
        <EmptyState
          icon="fa-solid fa-lock"
          title="Немає доступу"
          description="Переглядати активність клієнта може лише власник або реалтор зі спільним доступом."
          size="md"
        />
      </div>
    );
  }

  if (clientLoadState === "not-found") {
    return (
      <div className="feature-client-activity__state">
        <EmptyState
          icon="fa-solid fa-magnifying-glass"
          title="Клієнта не знайдено"
          description="Перевірте адресу сторінки або поверніться до списку клієнтів."
          size="md"
        />
      </div>
    );
  }

  if (clientLoadState === "error") {
    return (
      <div className="feature-client-activity__state">
        <EmptyState
          icon="fa-solid fa-triangle-exclamation"
          title="Не вдалося завантажити клієнта"
          description={loadError || "Спробуйте оновити сторінку."}
          size="md"
        />
      </div>
    );
  }

  if (!client) return null;

  const fullName = [client.first_name, client.last_name]
    .filter((part) => typeof part === "string" && part.trim().length > 0)
    .join(" ")
    .trim() || "Без імені";

  // ---------- JSX structure (Step 11) ----------
  //
  // Split layout. Three inline sections — client + notes edit on the
  // left, activity list + stale strip + refresh CTA on the right.
  // No orchestration wrappers, no slot architecture. Small inline
  // expressions only.

  return (
    <div
      className="feature-client-activity__split"
      data-feature="client-activity"
    >
      {/* ===== LEFT PANE — client summary + notes edit ===== */}
      <section
        className="feature-client-activity__pane feature-client-activity__pane--client"
        aria-labelledby="client-activity-left-title"
      >
        <header className="feature-client-activity__pane-header">
          <Heading
            level={2}
            variant="section"
            id="client-activity-left-title"
          >
            {fullName}
          </Heading>
          {client.phone ? (
            <Text as="p" size="sm" tone="secondary">
              {client.phone}
            </Text>
          ) : null}
          {client.email ? (
            <Text as="p" size="sm" tone="secondary">
              {client.email}
            </Text>
          ) : null}
        </header>

        <form
          className="ui-form"
          data-feature="client-activity-notes-form"
          onSubmit={handleSubmit}
          noValidate
        >
          <div className="ui-form__rows">
            <Field
              id="client-activity-notes"
              label="Нотатки"
              help={`До ${NOTES_MAX} символів. Простий текст.`}
              error={fieldErrors.notes}
            >
              <Textarea
                name="notes"
                value={clientDraft.notes}
                onChange={handleNotesChange}
                rows={8}
                maxLength={NOTES_MAX}
                placeholder="Особисті нотатки про клієнта…"
                disabled={saving}
              />
            </Field>
          </div>

          <div className="ui-form__messages">
            {formError ? (
              <InlineError className="ui-form__message">
                {formError}
              </InlineError>
            ) : null}
            {patchState === "saved" && !dirty ? (
              <Text
                as="span"
                size="xs"
                tone="success"
                className="ui-form__saved"
              >
                Нотатки збережено.
              </Text>
            ) : null}
          </div>

          <div className="ui-form__actions">
            <button
              type="submit"
              className="ui-form__button ui-form__button--primary"
              disabled={!canSubmit}
              data-loading={saving ? "true" : undefined}
            >
              {saving ? (
                <Spinner size="sm" label="Збереження" />
              ) : (
                "Зберегти нотатки"
              )}
            </button>
            <button
              type="button"
              className="ui-form__button ui-form__button--ghost"
              onClick={handleSubmitAndReturn}
              disabled={!canSubmit}
              data-loading={saving ? "true" : undefined}
            >
              Зберегти й повернутися
            </button>
          </div>
        </form>
      </section>

      {/* ===== RIGHT PANE — activity list + stale surface ===== */}
      <section
        className="feature-client-activity__pane feature-client-activity__pane--activity"
        aria-labelledby="client-activity-right-title"
      >
        <header className="feature-client-activity__pane-header">
          <Heading
            level={2}
            variant="section"
            id="client-activity-right-title"
            icon="fa-solid fa-clock-rotate-left"
          >
            Журнал взаємодій
          </Heading>
        </header>

        {activityLoadedAt ? (
          <div className="feature-client-activity__filter-status">
            <span className="feature-client-activity__filter-status-timestamp">
              Оновлено: {formatTime(activityLoadedAt)}
            </span>
          </div>
        ) : null}

        {/* Stale surface (Step 9). Renders only when the screen has
            explicitly flagged the list as potentially outdated. The
            CTA is manual; refresh timing belongs to the user. */}
        {stale ? (
          <div
            className="feature-client-activity__stale"
            role="status"
            aria-live="polite"
          >
            <div className="feature-client-activity__stale-text">
              <i
                className="fa-solid fa-triangle-exclamation"
                aria-hidden="true"
              />
              <span>{staleReason || "Дані можуть бути застарілими."}</span>
            </div>
            <button
              type="button"
              className="feature-client-activity__refresh-btn"
              onClick={handleRefreshActivity}
              disabled={refreshing}
              data-loading={refreshing ? "true" : undefined}
            >
              {refreshing ? (
                <Spinner size="sm" label="Оновлення" />
              ) : (
                <>
                  <i
                    className="fa-solid fa-arrows-rotate"
                    aria-hidden="true"
                  />
                  <span>Оновити список</span>
                </>
              )}
            </button>
          </div>
        ) : null}

        {/* Activity body. Mutually-exclusive render branches: loading,
            error (with manual retry), empty, populated. */}
        {activityLoadState === "loading" ? (
          <div className="feature-client-activity__activity-state">
            <Spinner size="md" label="Завантаження активності" />
          </div>
        ) : null}

        {activityLoadState === "error" ? (
          <div className="feature-client-activity__activity-error">
            <EmptyState
              icon="fa-solid fa-triangle-exclamation"
              title="Не вдалося завантажити активність"
              description={activityError || "Спробуйте оновити список."}
              size="sm"
              primaryAction={
                <button
                  type="button"
                  className="feature-client-activity__action-btn"
                  onClick={handleRefreshActivity}
                  disabled={refreshing}
                  data-loading={refreshing ? "true" : undefined}
                >
                  {refreshing ? (
                    <Spinner size="sm" label="Оновлення" />
                  ) : (
                    "Повторити"
                  )}
                </button>
              }
            />
          </div>
        ) : null}

        {activityLoadState === "loaded" && communications.length === 0 ? (
          <div className="feature-client-activity__activity-empty">
            <EmptyState
              icon="fa-solid fa-comment-dots"
              title="Поки що немає активності"
              description="Записи про дзвінки, листи та зустрічі з'являться тут."
              size="sm"
            />
          </div>
        ) : null}

        {activityLoadState === "loaded" && communications.length > 0 ? (
          <ul className="feature-client-activity__list">
            {communications.map((item) => {
              const typeLabel =
                COMMUNICATION_TYPE_LABELS[item.type] || item.type || "—";
              const directionLabel =
                COMMUNICATION_DIRECTION_LABELS[item.direction] ||
                item.direction ||
                "";
              const author =
                item.created_by &&
                (item.created_by.full_name ||
                  item.created_by.username ||
                  item.created_by.email);
              return (
                <li
                  key={item.id}
                  className="feature-client-activity__item"
                >
                  <div className="feature-client-activity__item-head">
                    <span className="feature-client-activity__item-type">
                      {typeLabel}
                    </span>
                    {directionLabel ? (
                      <span className="feature-client-activity__item-direction">
                        {directionLabel}
                      </span>
                    ) : null}
                    <span className="feature-client-activity__item-time">
                      {formatDateTime(item.created_at)}
                    </span>
                  </div>
                  {item.content ? (
                    <p className="feature-client-activity__item-content">
                      {item.content}
                    </p>
                  ) : null}
                  {author ? (
                    <p className="feature-client-activity__item-author">
                      Автор: {author}
                    </p>
                  ) : null}
                </li>
              );
            })}
          </ul>
        ) : null}
      </section>
    </div>
  );
}

export default ClientActivityScreenContent;
